import React from "react";
import styled from "styled-components";
import {
    Button,
    FormControl,
    IconButton,
    InputLabel,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    Select,
    SelectChangeEvent,
    TextField,
} from "@mui/material";
import { TextEntryFormControl } from "../TextEntryFormControl";
import { TextControlStyle } from "../../Utilities/FormDesignConstants";
import { StyledFormHelperText } from "./CommonStyled";
import RemoveIcon from "@mui/icons-material/Remove";
import { ValidationIssue } from "../../../rule-engine-core/Common/IDesignValidationProvider";
import { IssueData } from "../../Utilities/ControlRenderContext";
import { IDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/IDataDefinition";
import { StringDataDefinition } from "../../../rule-engine-core/Common/Data/Definitions/StringDataDefinition";
import { DataUtilities } from "../../../rule-engine-core/Common/DataUtilities";
import { UIStyleConstants } from "../../../rule-engine-core/Common/UIStyleConstants";
import { StateManager } from "../../../rule-engine-core/Common/StateManager";
import { UISpacedBoxContent } from "../../../CommonUI/BuildlingBlocks/UISpacedBoxContent";
import { UILabel } from "../../../CommonUI/BuildlingBlocks/UILabel";
import type { AppGlueAny } from "../../../rule-engine-core/Common/AppGlueAny";

export class SelectFormControl extends TextEntryFormControl {
    readonly __type: string = "Select";
    list: string[] = [];

    getRuntimeValidationIssues(): ValidationIssue[] {
        let issues = super.getRuntimeValidationIssues() ?? [];

        if (this.valueName) {
            let value = this.getFormDataValue(this.valueName);

            if (value) {
                if (this.list.length === 0 || this.list.indexOf(value) === -1) {
                    let issue = new ValidationIssue(
                        "Value [" + value + "] is not in selection list",
                        this.valueName,
                        this.id,
                    );
                    issues.push(issue);
                }
            }
        }

        return issues;
    }

    render() {
        let style = this.getFormContext()?.form?.defaultTextStyle;

        if (this.overrideStyle && this.style) style = this.style;

        let size: "medium" | "small" = this.getFormContext()?.form?.defaultTextSize ?? "medium";

        if (this.overrideStyle && this.size) size = this.size;

        const issueData: IssueData | null = this.getFormContext()!.getControlContext(this)!.getRuntimeIssueData();
        const issueText: string = issueData?.text || "";
        const customWidth = this.fullWidth ? "100%" : this.width ? `${this.width}px` : "200px";

        switch (style) {
            case TextControlStyle.LABELED:
                return (
                    <>
                        {this.label && <UILabel variant={"form_label"}>{this.label}</UILabel>}
                        <StyledFormControl
                            size={size}
                            data-size={size}
                            variant="outlined"
                            width={customWidth}
                            error={Boolean(issueText)}
                        >
                            <Select
                                defaultValue={"Pizza"}
                                value={this.valueName ? this.getFormDataValue(this.valueName) : ""}
                                native
                                onChange={this.handleChange}
                                data-testid={this.valueName}
                                ref={this.ref}
                            >
                                {this.list.map((item, index) => {
                                    return (
                                        <option
                                            value={item}
                                            key={index}
                                        >
                                            {item}
                                        </option>
                                    );
                                })}
                            </Select>
                            {(issueText || this.hintText) && (
                                <StyledFormHelperText
                                    error={Boolean(issueText)}
                                    data-testid={`${this.valueName || "selectbox"}-hinttext`}
                                >
                                    {issueText ? issueText : this.hintText}
                                </StyledFormHelperText>
                            )}
                        </StyledFormControl>
                    </>
                );
            case TextControlStyle.SHADED:
                return (
                    <StyledFormControl
                        size={size}
                        data-size={size}
                        variant="filled"
                        customstyle={"filled"}
                        width={customWidth}
                        error={Boolean(issueText)}
                    >
                        <InputLabel>{this.valueName}</InputLabel>
                        <Select
                            defaultValue={"Pizza"}
                            value={this.valueName ? this.getFormDataValue(this.valueName) : ""}
                            native
                            onChange={this.handleChange}
                            data-testid={this.valueName}
                            ref={this.ref}
                        >
                            {this.list.map((item, index) => {
                                return (
                                    <option
                                        value={item}
                                        key={index}
                                    >
                                        {item}
                                    </option>
                                );
                            })}
                        </Select>
                        {(issueText || this.hintText) && (
                            <StyledFormHelperText
                                error={Boolean(issueText)}
                                data-testid={`${this.valueName || "selectbox"}-hinttext`}
                            >
                                {issueText ? issueText : this.hintText}
                            </StyledFormHelperText>
                        )}
                    </StyledFormControl>
                );
            case TextControlStyle.OUTLINE:
                return (
                    <StyledFormControl
                        size={size}
                        data-size={size}
                        variant="outlined"
                        customstyle={"outlined"}
                        width={customWidth}
                        error={Boolean(issueText)}
                    >
                        <InputLabel>{this.valueName}</InputLabel>
                        <Select
                            defaultValue={"Pizza"}
                            value={this.valueName ? this.getFormDataValue(this.valueName) : ""}
                            native
                            onChange={this.handleChange}
                            data-testid={this.valueName}
                            ref={this.ref}
                        >
                            {this.list.map((item, index) => {
                                return (
                                    <option
                                        value={item}
                                        key={index}
                                    >
                                        {item}
                                    </option>
                                );
                            })}
                        </Select>
                        {(issueText || this.hintText) && (
                            <StyledFormHelperText
                                error={Boolean(issueText)}
                                data-testid={`${this.valueName || "selectbox"}-hinttext`}
                            >
                                {issueText ? issueText : this.hintText}
                            </StyledFormHelperText>
                        )}
                    </StyledFormControl>
                );
            case TextControlStyle.UNDERLINED:
                return (
                    <StyledFormControl
                        size={size}
                        data-size={size}
                        variant="standard"
                        customstyle={"standard"}
                        width={customWidth}
                        error={Boolean(issueText)}
                    >
                        <InputLabel>{this.valueName}</InputLabel>
                        <Select
                            defaultValue={"Pizza"}
                            value={this.valueName ? this.getFormDataValue(this.valueName) : ""}
                            native
                            onChange={this.handleChange}
                            data-testid={this.valueName}
                            ref={this.ref}
                        >
                            {this.list.map((item, index) => {
                                return (
                                    <option
                                        value={item}
                                        key={index}
                                    >
                                        {item}
                                    </option>
                                );
                            })}
                        </Select>
                        {(issueText || this.hintText) && (
                            <StyledFormHelperText
                                error={Boolean(issueText)}
                                data-testid={`${this.valueName || "selectbox"}-hinttext`}
                            >
                                {issueText ? issueText : this.hintText}
                            </StyledFormHelperText>
                        )}
                    </StyledFormControl>
                );
        }
    }

    getAddedData(): IDataDefinition | IDataDefinition[] | undefined {
        if (!this.valueName) return;

        let def = new StringDataDefinition();
        def.name = this.valueName;
        return def;
    }

    renderEditUI(): JSX.Element | null {
        return (
            <UISpacedBoxContent>
                {this.renderBaseDataControlEditor()}
                {this.renderTextControlBasePropertyEditor()}

                <List style={{ width: "100%" }}>
                    {this.list.map((s: string, idx: number) => (
                        <ListItem>
                            <ListItemText>
                                <TextField
                                    label="Option Label"
                                    value={s}
                                    size="small"
                                    onChange={(event) => {
                                        this.list[idx] = event.target.value;
                                        StateManager.changed(this);
                                    }}
                                    key={idx}
                                />
                            </ListItemText>
                            <ListItemSecondaryAction>
                                <IconButton
                                    color="secondary"
                                    size="small"
                                    onClick={() => {
                                        this.list.splice(idx, 1);
                                        StateManager.changed(this);
                                    }}
                                >
                                    <RemoveIcon />
                                </IconButton>
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                </List>
                <Button
                    sx={{ background: UIStyleConstants.BLUE_COLOR }}
                    onClick={() => {
                        this.list.push("");
                        StateManager.changed(this);
                    }}
                >
                    Add Item
                </Button>
                {this.renderTextStyleSelectionEditor()}
            </UISpacedBoxContent>
        );
    }

    getStorageData(): object {
        let retData = super.getStorageData();

        retData = DataUtilities.addStorageData(this, retData, "__type", "list");

        return retData;
    }

    setStorageData(data: object): void {
        super.setStorageData(data);

        DataUtilities.setStorageData(this, data, "__type", "list");
    }

    private handleChange = (event: SelectChangeEvent<AppGlueAny>) => {
        if (this.valueName) {
            this.setFormDataValue(this.valueName, event.target.value);
        }
    };
}

interface FormControlProps {
    variant: string;
    size: string;
    width?: string;
    customstyle?: string;
    error: boolean;
    children: React.ReactNode;
}

const StyledFormControl = styled(FormControl)`
    width: ${(props: FormControlProps) => props.width} !important;

    fieldset {
        border: none !important;
    }

    label {
        padding: 0 5px;
        background-color: ${({ customstyle }) => (customstyle === "outlined" ? UIStyleConstants.WHITE_COLOR : "unset")};
    }

    select {
        color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
        font-weight: 400 !important;
        line-height: 20px !important;
        border-radius: 5.65107px;
        background-color: ${({ customstyle }) =>
            customstyle === "filled" ? UIStyleConstants.GRAY_VERY_LIGHT_COLOR : "unset"};
        padding-left: ${({ customstyle }) => (customstyle !== "standard" ? "20px" : "0px")};
        padding-right: ${({ customstyle }) => (customstyle !== "standard" ? "20px" : "0px")};

        &:focus {
            border-radius: 5.65107px;
            border: ${({ customstyle }) =>
                    customstyle === "filled" || customstyle === "standard" ? "0px" : "1.35302px"}
                solid ${({ error }) => (error ? UIStyleConstants.RED_DARK_COLOR : UIStyleConstants.BLUE_GRAY_COLOR)};
        }

        border: ${({ customstyle }) => (customstyle === "filled" || customstyle === "standard" ? "0px" : "1.35302px")}
            solid ${({ error }) => (error ? UIStyleConstants.RED_DARK_COLOR : UIStyleConstants.GRAY_LIGHTER_COLOR)};
    }

    .MuiFormLabel-root.Mui-focused {
        color: ${({ error }) => (error ? UIStyleConstants.RED_DARK_COLOR : UIStyleConstants.TEXT_COLOR_DEFAULT)};
    }

    .MuiFormHelperText-contained {
        margin-left: 0px;
    }

    .muiselect-select.muiselect-select: focus {
        background-color: ${({ customstyle }) =>
            customstyle === "filled" ? UIStyleConstants.GRAY_VERY_LIGHT_COLOR : "unset"};
    }

    .MuiFilledInput-underline:after,
    .MuiInput-underline:after {
        border-bottom: 1px solid ${UIStyleConstants.BLUE_COLOR};
    }

    .MuiFilledInput-underline:before,
    .MuiInput-underline:before {
        border-bottom: 1px solid ${UIStyleConstants.GRAY_COLOR};
        opacity: 0.3;
    }
` as React.ComponentType<FormControlProps>;
